import React, { useState, useEffect } from 'react';
import {
  Plus, Trash2, Edit3, Save, X, ThumbsUp, BarChart3,
  TrendingUp, Users, CheckCircle, XCircle, Loader, Play, Pause,
  Heart, Star, Award
} from 'lucide-react';
import { getAuthHeaders } from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const styles = {
  container: {
    backgroundColor: '#0F0F0F',
    color: '#F5F5F5',
    padding: '24px'
  },
  card: {
    backgroundColor: '#1A1A1A',
    border: '1px solid #262626',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px'
  },
  button: {
    padding: '10px 16px',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #404040',
    borderRadius: '8px',
    backgroundColor: '#262626',
    color: '#F5F5F5',
    fontSize: '14px',
    outline: 'none',
    marginBottom: '12px'
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #404040',
    borderRadius: '8px',
    backgroundColor: '#262626',
    color: '#F5F5F5',
    fontSize: '14px',
    outline: 'none',
    marginBottom: '12px',
    cursor: 'pointer'
  },
  pollCard: {
    backgroundColor: '#262626',
    border: '1px solid #404040',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px',
    transition: 'all 0.2s'
  },
  badge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    marginRight: '8px'
  }
};

const AdminVotingTab = () => {
  const [view, setView] = useState('polls');
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPoll, setEditingPoll] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  // Engagement analytics state
  const [engagementData, setEngagementData] = useState(null);
  const [engagementPeriod, setEngagementPeriod] = useState('30d');
  const [engagementTab, setEngagementTab] = useState('votes');
  const [engagementLoading, setEngagementLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'media_request',
    isFeatured: false,
    endDate: '',
    options: [
      { title: '', type: 'movie', poster: '', year: '', genre: '' },
      { title: '', type: 'movie', poster: '', year: '', genre: '' }
    ]
  });

  useEffect(() => {
    fetchPolls();
    fetchAnalytics();
    fetchEngagement();
  }, []);

  useEffect(() => {
    fetchEngagement();
  }, [engagementPeriod]);

  const fetchPolls = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/admin/votes/polls`, { headers });
      const data = await response.json();

      if (data.success) {
        setPolls(data.polls);
      }
    } catch (error) {
      console.error('Error fetching polls:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/admin/votes/analytics/dashboard`, { headers });
      const data = await response.json();

      if (data.success) {
        setAnalytics(data.analytics);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchEngagement = async () => {
    setEngagementLoading(true);
    try {
      const headers = getAuthHeaders();
      const [votesRes, favoritesRes, channelsRes] = await Promise.all([
        fetch(`${API_URL}/admin/votes/analytics/media-engagement?period=${engagementPeriod}`, { headers }),
        fetch(`${API_URL}/admin/votes/analytics/media-favorites?period=${engagementPeriod}`, { headers }),
        fetch(`${API_URL}/admin/votes/analytics/channel-favorites?period=${engagementPeriod}`, { headers })
      ]);
      const [votes, favorites, channels] = await Promise.all([votesRes.json(), favoritesRes.json(), channelsRes.json()]);
      setEngagementData({
        votes: votes.data || [],
        favorites: favorites.data || [],
        channels: channels.data || []
      });
    } catch (err) {
      console.error('Error fetching engagement:', err);
    } finally {
      setEngagementLoading(false);
    }
  };

  const cleanFormData = (data) => ({
    ...data,
    options: data.options.map(opt => ({
      ...opt,
      year: opt.year !== '' ? opt.year : null,
      poster: opt.poster || null,
      thumbnail: opt.thumbnail || null,
      genre: opt.genre || null,
      channelId: opt.channelId || null,
      channelName: opt.channelName || null,
      videoId: opt.videoId || null,
      videoUrl: opt.videoUrl || null
    }))
  });

  const handleCreatePoll = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/admin/votes/polls`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cleanFormData(formData))
      });

      const data = await response.json();

      if (data.success) {
        alert('Poll created successfully!');
        setShowCreateForm(false);
        resetForm();
        fetchPolls();
        fetchAnalytics();
      } else {
        alert(data.error || 'Failed to create poll');
      }
    } catch (error) {
      console.error('Error creating poll:', error);
      alert('Failed to create poll');
    }
  };

  const handleEditPoll = (poll) => {
    // Load poll data into form
    setFormData({
      title: poll.title,
      description: poll.description || '',
      category: poll.category,
      isFeatured: poll.isFeatured || false,
      endDate: poll.endDate ? new Date(poll.endDate).toISOString().split('T')[0] : '',
      options: poll.options.map(opt => ({
        optionId: opt.optionId,
        title: opt.title,
        type: opt.type,
        poster: opt.poster || opt.thumbnail || '',
        year: opt.year || '',
        genre: opt.genre || '',
        channelId: opt.channelId || '',
        channelName: opt.channelName || '',
        videoId: opt.videoId || '',
        videoUrl: opt.videoUrl || ''
      }))
    });
    setEditingPoll(poll._id);
    setShowCreateForm(true);
  };

  const handleUpdatePoll = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/admin/votes/polls/${editingPoll}`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cleanFormData(formData))
      });

      const data = await response.json();

      if (data.success) {
        alert('Poll updated successfully!');
        setShowCreateForm(false);
        setEditingPoll(null);
        resetForm();
        fetchPolls();
        fetchAnalytics();
      } else {
        alert(data.error || 'Failed to update poll');
      }
    } catch (error) {
      console.error('Error updating poll:', error);
      alert('Failed to update poll');
    }
  };

  const handleTogglePoll = async (pollId) => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/admin/votes/polls/${pollId}/toggle`, {
        method: 'PATCH',
        headers
      });

      const data = await response.json();

      if (data.success) {
        fetchPolls();
        fetchAnalytics();
      } else {
        alert(data.error || 'Failed to toggle poll');
      }
    } catch (error) {
      console.error('Error toggling poll:', error);
      alert('Failed to toggle poll');
    }
  };

  const handleDeletePoll = async (pollId) => {
    if (!window.confirm('Are you sure you want to delete this poll? All votes will be lost.')) {
      return;
    }

    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/admin/votes/polls/${pollId}`, {
        method: 'DELETE',
        headers
      });

      const data = await response.json();

      if (data.success) {
        alert('Poll deleted successfully!');
        fetchPolls();
        fetchAnalytics();
      } else {
        alert(data.error || 'Failed to delete poll');
      }
    } catch (error) {
      console.error('Error deleting poll:', error);
      alert('Failed to delete poll');
    }
  };

  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, { title: '', type: 'movie', poster: '', year: '', genre: '' }]
    }));
  };

  const removeOption = (index) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const updateOption = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => i === index ? { ...opt, [field]: value } : opt)
    }));
  };

  // Fetch channel data from database
  const fetchChannelData = async (channelId, index) => {
    if (!channelId || channelId.trim() === '') return;

    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/admin/channels/${channelId}`, { headers });
      const data = await response.json();

      if (data.success && data.channel) {
        const channel = data.channel;
        const imageUrl = channel.avatar || channel.thumbnail || channel.thumbnailUrl;
        setFormData(prev => ({
          ...prev,
          options: prev.options.map((opt, i) => i === index ? {
            ...opt,
            title: channel.name || channel.title,
            channelId: channelId,
            channelName: channel.name || channel.title,
            thumbnail: imageUrl,
            poster: imageUrl,
            type: 'reactor_channel'
          } : opt)
        }));
        alert(`Channel data loaded: ${channel.name}`);
      } else {
        alert('Channel not found in database');
      }
    } catch (error) {
      console.error('Error fetching channel data:', error);
      alert('Failed to fetch channel data');
    }
  };

  // Fetch video data from database or URL
  const fetchVideoData = async (videoUrl, index) => {
    if (!videoUrl || videoUrl.trim() === '') return;

    // Extract video ID from URL
    const videoIdMatch = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    const videoId = videoIdMatch ? videoIdMatch[1] : videoUrl;

    try {
      const headers = getAuthHeaders();

      // Try to fetch from reactions database first
      const response = await fetch(`${API_URL}/reactions/videos/${videoId}`, { headers });
      const data = await response.json();

      if (data.success && data.video) {
        const video = data.video;
        setFormData(prev => ({
          ...prev,
          options: prev.options.map((opt, i) => i === index ? {
            ...opt,
            title: video.title,
            videoId: videoId,
            videoUrl: videoUrl,
            thumbnail: video.thumbnail || `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
            channelName: video.channelName || video.channelTitle,
            type: 'reaction_video'
          } : opt)
        }));
        alert(`Video data loaded: ${video.title}`);
      } else {
        // Fallback to YouTube thumbnail
        setFormData(prev => ({
          ...prev,
          options: prev.options.map((opt, i) => i === index ? {
            ...opt,
            videoId: videoId,
            videoUrl: videoUrl,
            thumbnail: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
            type: 'reaction_video'
          } : opt)
        }));
        alert('Video not found in database, but thumbnail loaded from YouTube');
      }
    } catch (error) {
      console.error('Error fetching video data:', error);
      alert('Failed to fetch video data');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'media_request',
      isFeatured: false,
      endDate: '',
      options: [
        { title: '', type: 'movie', poster: '', year: '', genre: '' },
        { title: '', type: 'movie', poster: '', year: '', genre: '' }
      ]
    });
  };

  if (loading) {
    return (
      <div style={{
        ...styles.container,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <Loader size={48} style={{ color: '#8B5CF6', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* View switcher: Polls management vs Analytics */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '26px', fontWeight: '700', margin: 0 }}>Voting & Polls</h1>
          <p style={{ fontSize: '13px', color: '#A3A3A3', margin: '6px 0 0' }}>
            {view === 'polls' ? 'Create and manage community polls' : 'Voting and engagement analytics'}
          </p>
        </div>
        <div style={{ display: 'inline-flex', gap: '6px', padding: '4px', borderRadius: '12px', border: '1px solid #404040', backgroundColor: '#1A1A1A' }}>
          {[
            { id: 'polls', label: 'Polls', icon: <ThumbsUp size={15} /> },
            { id: 'analytics', label: 'Analytics', icon: <BarChart3 size={15} /> }
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setView(item.id)}
              style={{
                ...styles.button,
                padding: '9px 18px',
                fontSize: '13px',
                backgroundColor: view === item.id ? '#8B5CF6' : 'transparent',
                color: view === item.id ? '#FFFFFF' : '#A3A3A3'
              }}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Analytics Dashboard */}
      {view === 'analytics' && analytics && (
        <div style={styles.card}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
            <BarChart3 size={24} style={{ display: 'inline', marginRight: '12px', color: '#8B5CF6' }} />
            Voting Analytics
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
            <div style={{ ...styles.pollCard, textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#8B5CF6', marginBottom: '8px' }}>
                {analytics.activePolls}
              </div>
              <div style={{ fontSize: '14px', color: '#A3A3A3' }}>Active Polls</div>
            </div>

            <div style={{ ...styles.pollCard, textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#10B981', marginBottom: '8px' }}>
                {analytics.totalPolls}
              </div>
              <div style={{ fontSize: '14px', color: '#A3A3A3' }}>Total Polls</div>
            </div>

            <div style={{ ...styles.pollCard, textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#F97316', marginBottom: '8px' }}>
                {analytics.totalVotes}
              </div>
              <div style={{ fontSize: '14px', color: '#A3A3A3' }}>Total Votes</div>
            </div>

            <div style={{ ...styles.pollCard, textAlign: 'center' }}>
              <div style={{ fontSize: '32px', fontWeight: '700', color: '#FBBF24', marginBottom: '8px' }}>
                {analytics.mostActivePoll ? analytics.mostActivePoll.totalVotes : 0}
              </div>
              <div style={{ fontSize: '14px', color: '#A3A3A3' }}>Most Active Poll Votes</div>
            </div>
          </div>
        </div>
      )}

      {/* Create Poll Button */}
      {view === 'polls' && (
      <div style={{ marginBottom: '24px' }}>
        <button
          style={{
            ...styles.button,
            backgroundColor: '#8B5CF6',
            color: 'white'
          }}
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? <X size={20} /> : <Plus size={20} />}
          {showCreateForm ? 'Cancel' : 'Create New Poll'}
        </button>
      </div>
      )}

      {/* Create/Edit Poll Form */}
      {view === 'polls' && showCreateForm && (
        <div style={styles.card}>
          <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>
            {editingPoll ? 'Edit Poll' : 'Create New Poll'}
          </h3>

          <input
            type="text"
            placeholder="Poll Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            style={styles.input}
          />

          <textarea
            placeholder="Poll Description (optional)"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            style={{ ...styles.input, minHeight: '80px', resize: 'vertical' }}
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
            <select
              value={formData.category}
              onChange={(e) => {
                const newCategory = e.target.value;
                // Auto-set option types based on category
                const defaultType =
                  newCategory === 'reactor' ? 'reactor_channel' :
                  newCategory === 'reaction_video' ? 'reaction_video' :
                  'movie';

                setFormData(prev => ({
                  ...prev,
                  category: newCategory,
                  options: prev.options.map(opt => ({ ...opt, type: defaultType }))
                }));
              }}
              style={styles.select}
            >
              <option value="media_request">Media Request</option>
              <option value="reaction_video">Reaction Video</option>
              <option value="reactor">Reactor</option>
              <option value="general">General</option>
            </select>

            <input
              type="date"
              placeholder="End Date (optional)"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              style={styles.input}
            />
          </div>

          {/* Featured / Standard toggle */}
          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '20px'
          }}>
            {[
              { value: false, label: 'Standard', desc: 'Shown in regular poll list' },
              { value: true, label: '★ Featured', desc: 'Highlighted at top of Vote tab' }
            ].map(opt => (
              <button
                key={String(opt.value)}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, isFeatured: opt.value }))}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  border: formData.isFeatured === opt.value
                    ? '2px solid #8B5CF6'
                    : '2px solid #404040',
                  borderRadius: '10px',
                  backgroundColor: formData.isFeatured === opt.value
                    ? 'rgba(139, 92, 246, 0.15)'
                    : '#262626',
                  color: formData.isFeatured === opt.value ? '#A78BFA' : '#9CA3AF',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '2px' }}>{opt.label}</div>
                <div style={{ fontSize: '11px', opacity: 0.7 }}>{opt.desc}</div>
              </button>
            ))}
          </div>

          <h4 style={{ fontSize: '16px', fontWeight: '600', marginTop: '24px', marginBottom: '16px' }}>
            Poll Options
          </h4>

          {formData.options.map((option, index) => (
            <div key={index} style={{ ...styles.pollCard, marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontWeight: '600', color: '#8B5CF6' }}>Option {index + 1}</span>
                {formData.options.length > 2 && (
                  <button
                    onClick={() => removeOption(index)}
                    style={{
                      ...styles.button,
                      padding: '6px 12px',
                      backgroundColor: '#DC2626',
                      color: 'white'
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px', color: '#A3A3A3', marginBottom: '6px', display: 'block' }}>
                  Type
                </label>
                <select
                  value={option.type}
                  onChange={(e) => updateOption(index, 'type', e.target.value)}
                  style={styles.select}
                >
                  <option value="movie">Movie</option>
                  <option value="tv">TV Show</option>
                  <option value="anime">Anime</option>
                  <option value="music">Music</option>
                  <option value="reactor_channel">Reactor Channel</option>
                  <option value="reaction_video">Reaction Video</option>
                </select>
              </div>

              {/* Channel ID Input with Load Button */}
              {option.type === 'reactor_channel' && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '12px', color: '#A3A3A3', marginBottom: '6px', display: 'block' }}>
                    Channel ID *
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder="Enter Channel ID"
                      value={option.channelId || ''}
                      onChange={(e) => updateOption(index, 'channelId', e.target.value)}
                      style={{ ...styles.input, marginBottom: 0, flex: 1 }}
                    />
                    <button
                      onClick={() => fetchChannelData(option.channelId, index)}
                      style={{
                        ...styles.button,
                        backgroundColor: '#10B981',
                        color: 'white',
                        padding: '10px 16px'
                      }}
                    >
                      Load Data
                    </button>
                  </div>
                </div>
              )}

              {/* Video URL Input with Load Button */}
              {option.type === 'reaction_video' && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '12px', color: '#A3A3A3', marginBottom: '6px', display: 'block' }}>
                    Video URL *
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder="Enter YouTube Video URL"
                      value={option.videoUrl || ''}
                      onChange={(e) => updateOption(index, 'videoUrl', e.target.value)}
                      style={{ ...styles.input, marginBottom: 0, flex: 1 }}
                    />
                    <button
                      onClick={() => fetchVideoData(option.videoUrl, index)}
                      style={{
                        ...styles.button,
                        backgroundColor: '#10B981',
                        color: 'white',
                        padding: '10px 16px'
                      }}
                    >
                      Load Data
                    </button>
                  </div>
                </div>
              )}

              {/* Title (auto-filled or manual) */}
              <input
                type="text"
                placeholder="Title *"
                value={option.title}
                onChange={(e) => updateOption(index, 'title', e.target.value)}
                style={styles.input}
              />

              {/* Year and Genre for media types */}
              {(option.type === 'movie' || option.type === 'tv' || option.type === 'anime' || option.type === 'music') && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <input
                    type="number"
                    placeholder="Year"
                    value={option.year}
                    onChange={(e) => updateOption(index, 'year', e.target.value)}
                    style={styles.input}
                  />
                  <input
                    type="text"
                    placeholder="Genre"
                    value={option.genre}
                    onChange={(e) => updateOption(index, 'genre', e.target.value)}
                    style={styles.input}
                  />
                </div>
              )}

              {/* Poster/Thumbnail (auto-filled or manual) */}
              <input
                type="text"
                placeholder="Poster/Thumbnail URL (auto-filled)"
                value={option.poster}
                onChange={(e) => updateOption(index, 'poster', e.target.value)}
                style={styles.input}
              />
            </div>
          ))}

          <button
            onClick={addOption}
            style={{
              ...styles.button,
              backgroundColor: '#262626',
              color: '#8B5CF6',
              marginBottom: '16px'
            }}
          >
            <Plus size={16} />
            Add Option
          </button>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button
              onClick={editingPoll ? handleUpdatePoll : handleCreatePoll}
              style={{
                ...styles.button,
                backgroundColor: '#8B5CF6',
                color: 'white',
                flex: 1
              }}
            >
              <Save size={20} />
              {editingPoll ? 'Update Poll' : 'Create Poll'}
            </button>

            <button
              onClick={() => {
                setShowCreateForm(false);
                setEditingPoll(null);
                resetForm();
              }}
              style={{
                ...styles.button,
                backgroundColor: '#404040',
                color: '#F5F5F5'
              }}
            >
              <X size={20} />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Polls List */}
      {view === 'polls' && (
      <div style={styles.card}>
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
          <ThumbsUp size={24} style={{ display: 'inline', marginRight: '12px', color: '#8B5CF6' }} />
          Manage Polls ({polls.length})
        </h2>

        {polls.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#A3A3A3' }}>
            <ThumbsUp size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
            <p>No polls created yet. Create your first poll to start collecting votes!</p>
          </div>
        ) : (
          polls.map(poll => (
            <div key={poll._id} style={{ ...styles.pollCard, borderLeft: `4px solid ${poll.isActive ? '#10B981' : '#DC2626'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                    {poll.title}
                  </h3>

                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    <span style={{
                      ...styles.badge,
                      backgroundColor: poll.isActive ? '#10B98120' : '#DC262620',
                      color: poll.isActive ? '#10B981' : '#DC2626',
                      border: `1px solid ${poll.isActive ? '#10B981' : '#DC2626'}`
                    }}>
                      {poll.isActive ? <><CheckCircle size={12} style={{ display: 'inline', marginRight: '4px' }} />Active</> : <><XCircle size={12} style={{ display: 'inline', marginRight: '4px' }} />Inactive</>}
                    </span>

                    {poll.isFeatured && (
                      <span style={{
                        ...styles.badge,
                        backgroundColor: '#F9731620',
                        color: '#F97316',
                        border: '1px solid #F97316'
                      }}>
                        ★ Featured
                      </span>
                    )}

                    <span style={{
                      ...styles.badge,
                      backgroundColor: '#8B5CF620',
                      color: '#8B5CF6',
                      border: '1px solid #8B5CF6'
                    }}>
                      {poll.category.replace('_', ' ')}
                    </span>

                    <span style={{
                      ...styles.badge,
                      backgroundColor: '#F9731620',
                      color: '#F97316',
                      border: '1px solid #F97316'
                    }}>
                      <Users size={12} style={{ display: 'inline', marginRight: '4px' }} />
                      {poll.totalVotes || 0} votes
                    </span>

                    <span style={{
                      ...styles.badge,
                      backgroundColor: '#FBBF2420',
                      color: '#FBBF24',
                      border: '1px solid #FBBF24'
                    }}>
                      {poll.uniqueVoters || 0} unique voters
                    </span>
                  </div>

                  {poll.description && (
                    <p style={{ fontSize: '14px', color: '#A3A3A3', marginBottom: '8px' }}>
                      {poll.description}
                    </p>
                  )}

                  <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                    {poll.options.length} options • Created {new Date(poll.createdAt).toLocaleDateString()}
                    {poll.endDate && ` • Ends ${new Date(poll.endDate).toLocaleDateString()}`}
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleEditPoll(poll)}
                    style={{
                      ...styles.button,
                      padding: '8px 12px',
                      backgroundColor: '#3B82F6',
                      color: 'white'
                    }}
                    title="Edit"
                  >
                    <Edit3 size={16} />
                  </button>

                  <button
                    onClick={() => handleTogglePoll(poll._id)}
                    style={{
                      ...styles.button,
                      padding: '8px 12px',
                      backgroundColor: poll.isActive ? '#FBBF24' : '#10B981',
                      color: 'white'
                    }}
                    title={poll.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {poll.isActive ? <Pause size={16} /> : <Play size={16} />}
                  </button>

                  <button
                    onClick={() => handleDeletePoll(poll._id)}
                    style={{
                      ...styles.button,
                      padding: '8px 12px',
                      backgroundColor: '#DC2626',
                      color: 'white'
                    }}
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Poll Options with Results */}
              <div style={{ marginTop: '16px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#A3A3A3' }}>
                  Results ({poll.analytics?.totalVotes || 0} votes):
                </h4>

                {poll.options.map((option, idx) => {
                  const totalVotes = poll.totalVotes || 0;
                  const percentage = totalVotes > 0 ? (option.voteCount / totalVotes) * 100 : 0;

                  return (
                    <div key={idx} style={{ marginBottom: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontSize: '13px', color: '#F5F5F5' }}>{option.title}</span>
                        <span style={{ fontSize: '13px', color: '#8B5CF6', fontWeight: '600' }}>
                          {option.voteCount} votes ({percentage.toFixed(1)}%)
                        </span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '6px',
                        backgroundColor: '#404040',
                        borderRadius: '3px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${percentage}%`,
                          backgroundColor: '#8B5CF6',
                          transition: 'width 0.3s'
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
      )}

      {/* Engagement Analytics Section */}
      {view === 'analytics' && (
      <div style={styles.card}>
        <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px' }}>
          <TrendingUp size={24} style={{ display: 'inline', marginRight: '12px', color: '#8B5CF6' }} />
          Engagement Analytics
        </h2>

        {/* Period Selector */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          {[
            { value: '7d', label: '7 Days' },
            { value: '30d', label: '30 Days' },
            { value: '90d', label: '90 Days' },
            { value: 'all', label: 'All Time' }
          ].map(p => (
            <button
              key={p.value}
              onClick={() => setEngagementPeriod(p.value)}
              style={{
                ...styles.button,
                backgroundColor: engagementPeriod === p.value ? '#8B5CF6' : '#262626',
                color: engagementPeriod === p.value ? 'white' : '#A3A3A3',
                border: engagementPeriod === p.value ? '1px solid #8B5CF6' : '1px solid #404040',
                padding: '8px 16px',
                fontSize: '13px'
              }}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Tab Selector */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid #404040', paddingBottom: '12px' }}>
          {[
            { value: 'votes', label: 'Most Voted Media', icon: <ThumbsUp size={14} /> },
            { value: 'favorites', label: 'Most Favorited Media', icon: <Heart size={14} /> },
            { value: 'channels', label: 'Top Channels', icon: <Star size={14} /> }
          ].map(t => (
            <button
              key={t.value}
              onClick={() => setEngagementTab(t.value)}
              style={{
                ...styles.button,
                backgroundColor: engagementTab === t.value ? '#8B5CF620' : 'transparent',
                color: engagementTab === t.value ? '#8B5CF6' : '#A3A3A3',
                border: 'none',
                borderBottom: engagementTab === t.value ? '2px solid #8B5CF6' : '2px solid transparent',
                borderRadius: '0',
                padding: '8px 16px',
                fontSize: '13px',
                gap: '6px'
              }}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {engagementLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Loader size={32} style={{ color: '#8B5CF6', animation: 'spin 1s linear infinite' }} />
          </div>
        ) : engagementData ? (
          <div>
            {/* Votes Tab */}
            {engagementTab === 'votes' && (
              engagementData.votes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#A3A3A3' }}>
                  <ThumbsUp size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
                  <p>No vote data for this period.</p>
                </div>
              ) : (
                engagementData.votes.map((item, idx) => (
                  <div key={idx} style={{ ...styles.pollCard, display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '28px', height: '28px', borderRadius: '50%',
                      backgroundColor: idx < 3 ? '#8B5CF6' : '#404040',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '12px', fontWeight: '700', color: 'white', flexShrink: 0
                    }}>
                      {idx + 1}
                    </div>
                    {item.posterUrl && (
                      <img
                        src={item.posterUrl}
                        alt={item.title}
                        style={{ width: '40px', height: '60px', borderRadius: '4px', objectFit: 'cover', flexShrink: 0 }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#F5F5F5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.title}
                      </div>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: item.type === 'movie' ? '#8B5CF620' : item.type === 'tv' ? '#10B98120' : item.type === 'music' ? '#FBBF2420' : '#F9731620',
                        color: item.type === 'movie' ? '#8B5CF6' : item.type === 'tv' ? '#10B981' : item.type === 'music' ? '#FBBF24' : '#F97316',
                        border: `1px solid ${item.type === 'movie' ? '#8B5CF6' : item.type === 'tv' ? '#10B981' : item.type === 'music' ? '#FBBF24' : '#F97316'}`,
                        fontSize: '10px', padding: '2px 8px', marginTop: '4px'
                      }}>
                        {item.type}
                      </span>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: '#8B5CF6' }}>{item.voteCount}</div>
                      <div style={{ fontSize: '11px', color: '#A3A3A3' }}>votes</div>
                    </div>
                  </div>
                ))
              )
            )}

            {/* Favorites Tab */}
            {engagementTab === 'favorites' && (
              engagementData.favorites.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#A3A3A3' }}>
                  <Heart size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
                  <p>No favorite data for this period.</p>
                </div>
              ) : (
                engagementData.favorites.map((item, idx) => (
                  <div key={idx} style={{ ...styles.pollCard, display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '28px', height: '28px', borderRadius: '50%',
                      backgroundColor: idx < 3 ? '#EC4899' : '#404040',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '12px', fontWeight: '700', color: 'white', flexShrink: 0
                    }}>
                      {idx + 1}
                    </div>
                    {item.poster && (
                      <img
                        src={item.poster}
                        alt={item.title}
                        style={{ width: '40px', height: '60px', borderRadius: '4px', objectFit: 'cover', flexShrink: 0 }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#F5F5F5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.title}
                      </div>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: item.type === 'movie' ? '#8B5CF620' : item.type === 'tv' ? '#10B98120' : item.type === 'music' ? '#FBBF2420' : '#F9731620',
                        color: item.type === 'movie' ? '#8B5CF6' : item.type === 'tv' ? '#10B981' : item.type === 'music' ? '#FBBF24' : '#F97316',
                        border: `1px solid ${item.type === 'movie' ? '#8B5CF6' : item.type === 'tv' ? '#10B981' : item.type === 'music' ? '#FBBF24' : '#F97316'}`,
                        fontSize: '10px', padding: '2px 8px', marginTop: '4px'
                      }}>
                        {item.type}
                      </span>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: '#EC4899' }}>{item.favoriteCount}</div>
                      <div style={{ fontSize: '11px', color: '#A3A3A3' }}>favorites</div>
                    </div>
                  </div>
                ))
              )
            )}

            {/* Channels Tab */}
            {engagementTab === 'channels' && (
              engagementData.channels.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#A3A3A3' }}>
                  <Star size={40} style={{ marginBottom: '12px', opacity: 0.5 }} />
                  <p>No channel favorite data for this period.</p>
                </div>
              ) : (
                engagementData.channels.map((item, idx) => (
                  <div key={idx} style={{ ...styles.pollCard, display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '28px', height: '28px', borderRadius: '50%',
                      backgroundColor: idx < 3 ? '#FBBF24' : '#404040',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '12px', fontWeight: '700', color: 'white', flexShrink: 0
                    }}>
                      {idx + 1}
                    </div>
                    {item.thumbnail && (
                      <img
                        src={item.thumbnail}
                        alt={item.channelName}
                        style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#F5F5F5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.channelName}
                      </div>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: '#FBBF2420',
                        color: '#FBBF24',
                        border: '1px solid #FBBF24',
                        fontSize: '10px', padding: '2px 8px', marginTop: '4px'
                      }}>
                        channel
                      </span>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: '#FBBF24' }}>{item.favoriteCount}</div>
                      <div style={{ fontSize: '11px', color: '#A3A3A3' }}>favorites</div>
                    </div>
                  </div>
                ))
              )
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#A3A3A3' }}>
            <Award size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
            <p>No engagement data available yet.</p>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default AdminVotingTab;
